import classNames from 'classnames';
import React, { useEffect, useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports
import { BoardModel } from 'app/gamma/src/types/models';

import { Analytics } from '@trello/atlassian-analytics';
import { smallestPreviewBiggerThan } from '@trello/image-previews';
import { StarredBoardButton } from './StarredBoardButton';
import { RouterLink } from 'app/src/components/RouterLink';
import { BoardTemplateBadge } from 'app/src/components/BoardTemplateBadge';
import styles from './CompactBoardTile.less';
import { hasUnreadActivity } from '@trello/boards';
import { UnsplashTracker } from '@trello/unsplash';
import { useCompactBoardTileQuery } from './CompactBoardTileQuery.generated';
import { makeLocalUrl } from '@trello/urls';

const getBackgroundStyle = (background?: BoardModel['background']) => {
  const css: React.CSSProperties = {};

  if (background) {
    const imageUrl = background.backgroundTile
      ? background.backgroundImage
      : // some old boards have not gone through image scaling,
        // so <board response>.prefs.backgroundImageScaled === null
        smallestPreviewBiggerThan(background.backgroundImageScaled, 400, 200)
          ?.url || background.backgroundImage;

    if (imageUrl) {
      css.backgroundImage = `url('${imageUrl}')`;
    }

    css.backgroundColor = background.backgroundTopColor || undefined;
  }

  return css;
};

export interface CompactBoardTileUnconnectedProps {
  readonly board: BoardModel;
  readonly focused?: boolean;
  readonly isStarred: boolean;
  readonly isTemplate?: boolean;
  readonly teamName?: string;
  toggleBoardStar: (boardId: string) => void;
}

export const CompactBoardTileUnconnected: React.FunctionComponent<CompactBoardTileUnconnectedProps> = ({
  board,
  focused,
  isStarred,
  isTemplate,
  teamName,
  toggleBoardStar,
}) => {
  const sendAnalytics = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'tile',
      actionSubjectId: 'compactBoardTile',
      source: 'boardsMenuInlineDialog',
      containers: {
        board: {
          id: board.id,
        },
      },
    });
  }, [board.id]);
  const { background, id, name, url } = board;

  useEffect(() => {
    background &&
      background.backgroundImage &&
      UnsplashTracker.trackOncePerInterval(background.backgroundImage);
  }, [background]);

  const isUnread = hasUnreadActivity(board);
  const boardNameStyles = classNames(styles.boardName, styles.boardNameBold);
  const backgroundStyle = getBackgroundStyle(background);
  const boardBackgroundStyles = classNames(
    styles.boardBackground,
    background && background.backgroundTile && styles.modTiled,
  );
  const containerStyles = classNames(
    styles.boardTile,
    focused && styles.isNavigationFocused,
  );
  const boardLinkStyles = classNames(styles.boardLink);
  const boardDescriptionStyles = classNames(
    styles.boardDescription,
    teamName && styles.fullHeight,
  );
  const starStyles = classNames(
    styles.utilityAction,
    isStarred && styles.isStarred,
    styles.actionSmall,
  );
  const unreadStyles = classNames(
    styles.unreadMarker,
    isUnread && styles.isUnread,
    isStarred && styles.isStarred,
  );
  const actionsWrapperStyles = classNames(
    styles.actionsWrapper,
    isUnread && styles.isUnread,
    isStarred && styles.isStarred,
  );

  return (
    <div className={containerStyles} key={id}>
      <RouterLink
        href={url || `/b/${id}`}
        className={boardLinkStyles}
        // eslint-disable-next-line react/jsx-no-bind
        onDragStart={(e) => e.preventDefault()}
        title={name}
        onClick={sendAnalytics}
      >
        <div className={boardBackgroundStyles} style={backgroundStyle} />
        <div className={styles.boardThumbnail} style={backgroundStyle} />
        <div className={boardDescriptionStyles}>
          <div className={boardNameStyles}>{name}</div>
          {teamName && <div className={styles.boardSubName}>{teamName}</div>}
        </div>
        {isTemplate && (
          <div className={styles.boardTemplateBadgeContainer}>
            <BoardTemplateBadge />
          </div>
        )}
        <div className={actionsWrapperStyles}>
          {isUnread && <div className={unreadStyles} />}
          <div className={starStyles}>
            <StarredBoardButton
              idBoard={id}
              isStarred={isStarred}
              toggleBoardStar={toggleBoardStar}
            />
          </div>
        </div>
      </RouterLink>
    </div>
  );
};

interface CompactBoardTileProps {
  readonly idBoard: string;
  readonly isStarred: boolean;
  readonly showTeamName?: boolean;
  readonly focused?: boolean;
  toggleBoardStar: (boardId: string) => void;
}

export const CompactBoardTile = ({
  idBoard,
  isStarred,
  focused,
  toggleBoardStar,
}: CompactBoardTileProps) => {
  const { data } = useCompactBoardTileQuery({
    variables: { idBoard },
  });
  if (data?.board === undefined) {
    return null;
  }
  const board: BoardModel = {
    background: {
      backgroundBottomColor: data.board?.prefs?.backgroundBottomColor || null,
      backgroundBrightness: data.board?.prefs?.backgroundBrightness || 'light',
      backgroundTopColor: data.board?.prefs?.backgroundTopColor || null,
      backgroundTile: !!data.board?.prefs?.backgroundTile,
      backgroundImage: data.board?.prefs?.backgroundImage || null,
      backgroundImageScaled: (
        data.board?.prefs?.backgroundImageScaled || []
      ).map((backgroundImageScaled) => ({
        // This casting is happening because undefined was what was previously
        // getting returned from the REST API. Upon converting from Redux to GraphQL,
        // keeping this value ensures safety of the consuming components.
        scaled: (undefined as unknown) as boolean,
        ...backgroundImageScaled,
      })),
    },
    dateLastActivity: data.board?.dateLastActivity
      ? new Date(data.board?.dateLastActivity)
      : undefined,
    dateLastView: data.board?.dateLastView
      ? new Date(data.board?.dateLastView)
      : undefined,
    id: data.board?.id || '',
    idOrganization: data.board?.organization?.id,
    name: data.board?.name,
    url: makeLocalUrl(data.board?.url || ''),
  };
  const teamName = data.board?.organization?.displayName;
  const isBoardTemplate = !!data.board?.prefs?.isTemplate;

  return board ? (
    <CompactBoardTileUnconnected
      board={board}
      teamName={teamName}
      isStarred={isStarred}
      isTemplate={isBoardTemplate}
      focused={focused}
      toggleBoardStar={toggleBoardStar}
    />
  ) : null;
};
